/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import { flexRender, Table } from "@tanstack/react-table";
import { Table as BSTable, Button } from "flowbite-react";
import { Filter } from "./TableFilter";
import { FaSortUp, FaSortDown } from "react-icons/fa";

export default function PaginatedTable<T extends { [key: string]: any }>({
  table,
  dataCount,
  createChildTable,
  subRowProp,
  columnResizeMode,
  showFilters = false,
  showPagination = true,
  showFooter = false,
  striped = true,
  highlightRow = false,
  selectableRow = false,
}: {
  table: Table<T>;
  dataCount: number;
  createChildTable?: (data: any) => JSX.Element;
  subRowProp?: keyof T;
  columnResizeMode: string;
  showFilters?: boolean;
  showPagination?: boolean;
  showFooter?: boolean;
  striped?: boolean;
  highlightRow?: boolean;
  selectableRow?: boolean;
}) {
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = table.getState().pagination.pageSize;
  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const totalPages = Math.max(1, Math.ceil(filteredRowCount / pageSize));

  const scrollToTop = () => {
    document
      .querySelector("#scroll-anchor")
      ?.scrollIntoView({ behavior: "smooth" });
  };
  const [windowWidth, setWindowWidth] = useState(window.innerWidth); // <-- add this

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768; // dynamic instead of static

  return (
    <>
      <div id="scroll-anchor" />

      {!isMobile ? (
        <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 200px)" }}>
          <BSTable style={{ position: "relative" }} striped={striped}>
            <thead style={{ position: "sticky", top: 0 }}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        position: "relative",
                        width: header.getSize(),
                        minWidth: "75px",
                        padding: "5px 7px",
                        backgroundColor: "white", // Add this line
                        zIndex: 1, // Optional: helps with stacking context
                        top: 0, // Ensures sticky position works correctly
                      }}
                    >
                      {!header.isPlaceholder && (
                        <div>
                          <div
                            className={
                              header.column.getCanSort()
                                ? "cursor-pointer select-none"
                                : ""
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: <FaSortUp style={{ marginLeft: "1rem" }} />,
                              desc: (
                                <FaSortDown style={{ marginLeft: "1rem" }} />
                              ),
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                          {showFilters && header.column.getCanFilter() ? (
                            <Filter column={header.column} table={table} />
                          ) : showFilters ? (
                            <div style={{ height: "31px" }} />
                          ) : null}
                        </div>
                      )}
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={`resizer ${
                          header.column.getIsResizing() ? "isResizing" : ""
                        }`}
                        style={{
                          cursor: "col-resize",
                          transform:
                            columnResizeMode === "onEnd" &&
                            header.column.getIsResizing()
                              ? `translateX(${
                                  table.getState().columnSizingInfo.deltaOffset
                                }px)`
                              : "",
                        }}
                      />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody style={{ overflow: "visible" }}>
              {table.getRowModel().rows.map((row, i) => (
                <React.Fragment key={i}>
                  <tr>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          maxWidth: cell.column.getSize(),
                          overflow: "hidden",
                          whiteSpace: "normal",
                          textOverflow: "ellipsis",
                          padding: "5px 7px",
                          backgroundColor:
                            row.getIsSelected() && highlightRow ? "orange" : "",
                        }}
                        onClick={() => selectableRow && row.toggleSelected()}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                  {createChildTable && subRowProp && row.getIsExpanded() && (
                    <tr>
                      <td colSpan={table.getFlatHeaders().length}>
                        {createChildTable(row.original[subRowProp])}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            {showFooter && (
              <tfoot>
                {table.getFooterGroups().map((footerGroup) => (
                  <tr key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.footer,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </tfoot>
            )}
          </BSTable>
        </div>
      ) : (
        <div className="grid gap-3">
          {table.getRowModel().rows.map((row, i) => (
            <div key={i} className="border p-3 rounded shadow-md">
              {row.getVisibleCells().map((cell) => {
                const header = table
                  .getHeaderGroups()[0]
                  .headers.find((h) => h.column.id === cell.column.id);
                return (
                  <div key={cell.id} className="mb-1">
                    <strong>
                      {header
                        ? flexRender(cell.column.columnDef.header, {
                            table,
                            column: cell.column,
                            header,
                          })
                        : cell.column.id}
                      :
                    </strong>

                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <footer
        style={{
          position: "sticky",
          minWidth: "100%",
          bottom: 0,
          paddingTop: 2,
          backgroundColor: "white",
        }}
      >
        {showPagination && table.getRowModel().rows.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 p-2">
            <div>
              Showing {table.getState().pagination.pageIndex * pageSize + 1}–
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * pageSize,
                filteredRowCount
              )}{" "}
              of {filteredRowCount} results
            </div>
            <div className="flex gap-1 items-center">
              <Button
                size="xs"
                type="button"
                className="buttonMain"
                disabled={!table.getCanPreviousPage()}
                onClick={() => {
                  table.setPageIndex(0);
                  setCurrentPage(1);
                  scrollToTop();
                }}
              >
                First
              </Button>
              <Button
                size="xs"
                type="button"
                className="buttonMain"
                disabled={!table.getCanPreviousPage()}
                onClick={() => {
                  table.previousPage();
                  setCurrentPage((prev) => Math.max(prev - 1, 1));
                  scrollToTop();
                }}
              >
                Prev
              </Button>
              <span>
                Page <strong>{currentPage}</strong> of{" "}
                <strong>{totalPages}</strong>
              </span>
              <Button
                size="xs"
                type="button"
                className="buttonMain"
                disabled={!table.getCanNextPage()}
                onClick={() => {
                  table.nextPage();
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages));
                  scrollToTop();
                }}
              >
                Next
              </Button>
              <Button
                size="xs"
                type="button"
                className="buttonMain"
                disabled={!table.getCanNextPage()}
                onClick={() => {
                  table.setPageIndex(totalPages - 1);
                  setCurrentPage(totalPages);
                  scrollToTop();
                }}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </footer>
    </>
  );
}
